import { getCountry, getCurrency, getIso, toBcp47LanguageTag } from '@pkg/utilities/i18n';
import type { ISO } from '@pkg/config/languages/countryIsoCodes';
import { getWikidataUrlByCountryIso } from '@pkg/config/languages/countryIsoToWikidata';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { prepareJsonLdForSerialize } from './jsonLdGraph';

const ORGANIZATION_NAME = 'Too Good To Go';

export type JsonLdPageType = 'WebPage' | 'AboutPage' | 'ContactPage';

export interface ContactPointConfig {
  contactType?: string;
  email?: string;
  url?: string;
}

export interface BuildPageJsonLdInput {
  siteBase: string;
  locale: LOCALE_CODE | string;
  pageType: JsonLdPageType;
  pageTitle: string;
  pageDescription?: string;
  websiteUrl: string;
  pageUrl: string;
  logoUrl: string;
  sameAs?: string[];
  appleStoreUrl?: string;
  playStoreUrl?: string;
  appDescription?: string;
  iosRatingValue?: string;
  iosRatingCount?: string;
  androidRatingValue?: string;
  androidRatingCount?: string;
  contactPoint?: ContactPointConfig | null;
}

/**
 * Schema.org @type for the page node.
 * Always include WebPage (alone or as multi-type) so Rich Results that look for
 * WebPage still match About/Contact locales — not only en-au crawls.
 */
export function resolvePageSchemaType(
  pageType: JsonLdPageType,
): JsonLdPageType | [JsonLdPageType, 'WebPage'] {
  if (pageType === 'WebPage') {
    return 'WebPage';
  }
  return [pageType, 'WebPage'];
}

function toAggregateRating(value?: string, count?: string) {
  const ratingValue = Number(value);
  const ratingCount = Number(count);
  if (
    !Number.isFinite(ratingValue) ||
    !Number.isFinite(ratingCount) ||
    ratingValue <= 0 ||
    ratingCount <= 0
  ) {
    return undefined;
  }

  return {
    '@type': 'AggregateRating' as const,
    ratingValue: ratingValue.toFixed(1),
    ratingCount: String(Math.round(ratingCount)),
    bestRating: '5',
    worstRating: '1',
  };
}

/**
 * Pure Organization / WebSite / WebPage / MobileApplication @graph builder.
 * Locale-scopes website + app @ids so crawlers merging locales cannot collide.
 */
export function buildPageJsonLdGraph(input: BuildPageJsonLdInput) {
  const siteBase = input.siteBase.replace(/\/$/, '');
  const locale = String(input.locale);
  const countryName = getCountry(locale as LOCALE_CODE);
  const currency = getCurrency(locale as LOCALE_CODE);
  const countryIso = getIso(locale as LOCALE_CODE) as ISO;
  const wikidataUrl = getWikidataUrlByCountryIso(countryIso);
  const inLanguage = toBcp47LanguageTag(locale as LOCALE_CODE);

  const orgId = `${siteBase}/#organization`;
  const logoId = `${siteBase}/#logo`;
  const websiteId = `${siteBase}/#website-${locale}`;
  const webpageId = `${input.pageUrl}#webpage`;
  const downloadUrl = input.appleStoreUrl || input.playStoreUrl || '';

  const mobileAppBase = {
    '@type': 'MobileApplication' as const,
    name: ORGANIZATION_NAME,
    url: input.websiteUrl,
    publisher: { '@id': orgId },
    applicationCategory: 'Food & Drink',
    inLanguage,
    areaServed: {
      '@type': 'Country' as const,
      name: countryName,
      sameAs: wikidataUrl,
    },
    offers: {
      '@type': 'Offer' as const,
      price: '0',
      priceCurrency: currency,
    },
    ...(input.appDescription && { description: input.appDescription }),
    ...(downloadUrl && { downloadUrl }),
  };

  const iosAggregateRating = toAggregateRating(
    input.iosRatingValue,
    input.iosRatingCount,
  );
  const androidAggregateRating = toAggregateRating(
    input.androidRatingValue,
    input.androidRatingCount,
  );

  const pageNode: Record<string, unknown> = {
    '@type': resolvePageSchemaType(input.pageType),
    '@id': webpageId,
    url: input.pageUrl,
    name: input.pageTitle,
    ...(input.pageType !== 'ContactPage' &&
      input.pageDescription && { description: input.pageDescription }),
    isPartOf: { '@id': websiteId },
    inLanguage,
  };

  if (
    input.pageType === 'ContactPage' &&
    input.contactPoint &&
    (input.contactPoint.email || input.contactPoint.url)
  ) {
    pageNode.mainEntity = {
      '@type': 'ContactPoint',
      contactType: input.contactPoint.contactType ?? 'customer support',
      ...(input.contactPoint.email && { email: input.contactPoint.email }),
      ...(input.contactPoint.url && { url: input.contactPoint.url }),
    };
  }

  const graph = [
    {
      '@type': 'Organization',
      '@id': orgId,
      name: ORGANIZATION_NAME,
      ...(siteBase && { url: siteBase }),
      logo: {
        '@type': 'ImageObject',
        '@id': logoId,
        url: input.logoUrl,
        width: 512,
        height: 512,
      },
      image: { '@id': logoId },
      sameAs: input.sameAs ?? [],
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: input.websiteUrl,
      name: ORGANIZATION_NAME,
      publisher: { '@id': orgId },
      inLanguage,
    },
    pageNode,
    // One MobileApplication per store — do not blend iOS/Android ratings (Google review-snippet rules).
    // Locale-scoped @ids avoid cross-locale crawler merges treating global #app-* as duplicates.
    {
      ...mobileAppBase,
      '@id': `${siteBase}/#app-ios-${locale}`,
      operatingSystem: 'IOS',
      ...(iosAggregateRating && { aggregateRating: iosAggregateRating }),
    },
    {
      ...mobileAppBase,
      '@id': `${siteBase}/#app-android-${locale}`,
      operatingSystem: 'ANDROID',
      ...(androidAggregateRating && { aggregateRating: androidAggregateRating }),
    },
  ];

  return prepareJsonLdForSerialize({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
}
